import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface InlineEditableProps {
  value: string;
  isEditing: boolean;
  onSave: (newValue: string) => void;
  onCancel: () => void;
  className?: string;
  inputClassName?: string;
}

/**
 * Inline editable text component.
 *
 * When `isEditing` is false, renders children as plain text.
 * When `isEditing` is true, renders an input field pre-filled with `value`.
 *
 * - Enter or blur → saves (if non-empty and changed)
 * - Escape → cancels
 * - Empty input on blur/enter → cancels (no save)
 */
export function InlineEditable({
  value,
  isEditing,
  onSave,
  onCancel,
  className,
  inputClassName,
}: InlineEditableProps) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  useEffect(() => {
    if (isEditing) {
      setDraft(value);
      committedRef.current = false;
      // Focus and select on next tick (after render)
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isEditing, value]);

  const handleCommit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    } else {
      onCancel();
    }
  };

  if (!isEditing) {
    return <span className={cn("truncate", className)}>{value}</span>;
  }

  return (
    <Input
      ref={inputRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={handleCommit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleCommit();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          if (!committedRef.current) {
            committedRef.current = true;
            onCancel();
          }
        }
      }}
      className={cn("h-auto px-1 py-0 text-[inherit] leading-[inherit]", inputClassName)}
    />
  );
}
